/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useMemo } from '@web-stories-wp/react';
import {
  createSolid,
  generatePatternStyles,
  hasGradient,
} from '@web-stories-wp/patterns';
import {
  dataToEditorX,
  dataToEditorY,
  dataToFontSizeY as dataToFontSize,
} from '@web-stories-wp/units';
import classnames from 'classnames';

/**
 * Internal dependencies
 */
import StoryPropTypes from '../../types';
import { getHTMLFormatters } from '../../components/richText/htmlManipulation';
import { BACKGROUND_TEXT_MODE } from '../../constants';
import { generateParagraphTextStyle, getHighlightLineheight } from './util';
import { calcFontMetrics } from './utils';
import {
  getMeasuredLineBreakContent,
  stripColorStylesFromMeasuredLineBreakContent,
} from './utils/getMeasuredLineBreakContent';
import {
  collectTextFontUsage,
  hasEffectiveInlineLineHeightRuns,
  materializeTextContent,
} from './utils/mixedTypography';
import { useFont } from '../../app';
import getListRenderContent from './utils/getListRenderContent';
import { hasSemanticList } from './utils/listStyle';
import useSanitizedTextElement from './utils/useSanitizedTextElement';
import { getTextTextureTransform } from './utils/getTextTextureTransform';

/**
 * Renders DOM for the text output based on the provided unit converters.
 *
 * @param {Object<*>} props Component props.
 * @param {Object<*>} props.element Story element.
 * @param {Function} props.dataToStyleX dataToStyleX function.
 * @param {Function} props.dataToStyleY dataToStyleY function.
 * @param {Function} props.dataToFontSizeY dataToFontSizeY function. Falls back to dataToStyleY if not provided.
 * @param {Function} props.dataToInlineFontSizeY Converts inline font sizes relative to the text box font size. Falls back to dataToFontSizeY if not provided.
 * @param {Function} props.dataToPaddingX dataToPaddingX function. Falls back to dataToStyleX if not provided.
 * @param {Function} props.dataToPaddingY dataToPaddingY function. Falls back to dataToStyleX if not provided.
 * @param {Function} props.dataToListStyleX Converts horizontal descendant-list dimensions.
 * @param {Function} props.dataToListStyleY Converts vertical descendant-list dimensions.
 * @param {Function} props.dataToListFontSizeY Converts descendant-list marker font sizes.
 * @param {Function} props.dataToListMarkerStyleX Converts horizontal marker dimensions.
 * @param {Function} props.dataToListMarkerStyleY Converts vertical marker dimensions.
 * @param {string} props.className Class name.
 * @return {*} Rendered component.
 */
export function TextOutputWithUnits({
  element: untrustedElement,
  dataToStyleX,
  dataToStyleY,
  dataToFontSizeY,
  dataToInlineFontSizeY,
  dataToPaddingX,
  dataToPaddingY,
  dataToListStyleX,
  dataToListStyleY,
  dataToListFontSizeY,
  dataToListMarkerStyleX,
  dataToListMarkerStyleY,
  className,
}) {
  const element = useSanitizedTextElement(untrustedElement);
  const { loadedFonts = [] } = useFont(({ state: { loadedFonts } }) => ({
    loadedFonts,
  }));

  const fontUsage = useMemo(() => collectTextFontUsage(element), [element]);
  const usedRenderFamilies = useMemo(
    () => new Set(fontUsage.map(({ renderFamily }) => renderFamily)),
    [fontUsage]
  );
  const fontLoaded = useMemo(
    () =>
      loadedFonts.filter((loadedFont) =>
        usedRenderFamilies.has(
          loadedFont.renderFamily || loadedFont.font.family
        )
      ),
    [loadedFonts, usedRenderFamilies]
  );

  const {
    content: storedContent,
    backgroundColor,
    backgroundTextMode,
    padding,
    borderRadius,
    ...rest
  } = element;
  if (!dataToFontSizeY) {
    dataToFontSizeY = dataToStyleY;
  }
  if (!dataToInlineFontSizeY) {
    dataToInlineFontSizeY = dataToFontSizeY;
  }
  if (!dataToPaddingX) {
    dataToPaddingX = dataToStyleX;
  }
  if (!dataToPaddingY) {
    dataToPaddingY = dataToStyleY;
  }
  if (!dataToListStyleX) {
    dataToListStyleX = dataToStyleX;
  }
  if (!dataToListStyleY) {
    dataToListStyleY = dataToStyleY;
  }
  if (!dataToListFontSizeY) {
    dataToListFontSizeY = dataToFontSizeY;
  }
  if (!dataToListMarkerStyleX) {
    dataToListMarkerStyleX = dataToListStyleX;
  }
  if (!dataToListMarkerStyleY) {
    dataToListMarkerStyleY = dataToListStyleY;
  }
  const rawContent = useMemo(
    () =>
      materializeTextContent(
        { ...element, content: storedContent },
        { fontSizeToCss: dataToInlineFontSizeY }
      ),
    [dataToInlineFontSizeY, element, storedContent]
  );
  const hasList = hasSemanticList(rawContent);
  const supportedBackgroundTextMode =
    hasList && backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
      ? BACKGROUND_TEXT_MODE.FILL
      : backgroundTextMode;
  const outputElement = useMemo(
    () =>
      supportedBackgroundTextMode === backgroundTextMode
        ? element
        : { ...element, backgroundTextMode: supportedBackgroundTextMode },
    [backgroundTextMode, element, supportedBackgroundTextMode]
  );
  const hasMixedLineHeight = useMemo(
    () => !hasList && hasEffectiveInlineLineHeightRuns(outputElement),
    [hasList, outputElement]
  );
  const paddingStyles = {
    vertical: dataToPaddingY(padding.vertical),
    horizontal: dataToPaddingX(padding.horizontal),
  };

  const bgColor =
    supportedBackgroundTextMode !== BACKGROUND_TEXT_MODE.NONE
      ? generatePatternStyles(backgroundColor, 'background', null, element)
      : undefined;

  const hasTextGradient = hasGradient(element.color);
  const hasTextTexture = Boolean(element.textTexture?.src);
  const gradientFallbackColor = hasTextGradient
    ? getGradientFallbackColor(element.color)
    : null;
  const gradientTextStyle = hasTextGradient
    ? {
        ...generatePatternStyles(element.color, 'background', null, element),
        color: gradientFallbackColor || '#000000',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }
    : null;
  const textureTransform = getTextTextureTransform(
    element.textTexture,
    element.width,
    element.height
  );
  const textureTextStyle = hasTextTexture
    ? {
        backgroundImage: `url("${element.textTexture.src.replace(
          /"/g,
          '\\"'
        )}")`,
        backgroundSize: textureTransform
          ? `${(textureTransform.width / element.width) * 100}% ${
              (textureTransform.height / element.height) * 100
            }%`
          : getTextureBackgroundSize(element.textTexture.sizing),
        backgroundPosition: getTextureBackgroundPosition(
          element.textTexture.position
        ),
        backgroundRepeat: 'no-repeat',
        ...(textureTransform ? { backgroundOrigin: 'border-box' } : {}),
        color: getTextureFallbackColor(element.textTexture, element.color),
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }
    : null;

  const { marginOffsetTop } = useMemo(
    () =>
      calcFontMetrics({
        element: {
          content: element.content,
          fontReferences: element.fontReferences,
          color: element.color,
          font: element.font,
          fontSize: element.fontSize,
          fontStyle: element.fontStyle,
          fontWeight: element.fontWeight,
          lineHeight: element.lineHeight,
          verticalTrim: element.verticalTrim,
          width: element.width,
          backgroundTextMode: supportedBackgroundTextMode,
          padding: element.padding,
          listStyle: element.listStyle,
        },
        dataToEditorX,
        dataToEditorY,
      }),
    [
      element.content,
      element.fontReferences,
      element.color,
      element.font,
      element.fontSize,
      element.fontStyle,
      element.fontWeight,
      element.lineHeight,
      element.verticalTrim,
      element.width,
      supportedBackgroundTextMode,
      element.padding,
      element.listStyle,
      dataToEditorX,
      dataToEditorY,
      fontLoaded,
    ]
  );

  const {
    dataToEditorX: _dataToEditorX,
    dataToEditorY: _dataToEditorY,
    font: _font,
    textShadow,
    boxShadow,
    ...styles
  } = generateParagraphTextStyle(
    rest,
    dataToStyleX,
    dataToStyleY,
    dataToFontSizeY,
    marginOffsetTop,
    element,
    dataToPaddingY,
    true /** isOutput */
  );

  const baseFillStyle = {
    ...styles,
    color: '#000000',
    lineHeight: hasMixedLineHeight ? 0 : styles.lineHeight,
    padding: `${paddingStyles.vertical} ${paddingStyles.horizontal}`,
    overflowWrap: 'break-word',
  };
  const fillStyle = {
    ...baseFillStyle,
    ...(textShadow ? { textShadow } : null),
    ...(gradientTextStyle || null),
    ...(textureTextStyle || null),
  };

  const unitlessPaddingVertical = parseFloat(dataToStyleY(padding.vertical));
  const unitlessFontSize = parseFloat(dataToStyleY(rest.fontSize));

  const lineHeight = getHighlightLineheight(
    rest.lineHeight,
    unitlessPaddingVertical / unitlessFontSize,
    'em'
  );
  const highlightStyle = {
    ...baseFillStyle,
    ...bgColor,
    margin: 0,
    padding: 0,
    background: 'none',
    lineHeight: hasMixedLineHeight ? 0 : lineHeight,
    overflowWrap: 'break-word',
  };

  const highlightCloneStyle = {
    ...highlightStyle,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  };

  const highlightForegroundStyle = {
    ...highlightCloneStyle,
    ...(gradientTextStyle || null),
    ...(textureTextStyle || null),
  };

  const marginStyle = useMemo(
    () => ({
      display: 'block',
      position: 'relative',
      left: 0,
      top: '0',
      margin: `${dataToPaddingY(-marginOffsetTop)} 0`,
      /* stylelint-disable-next-line */
      WebkitBoxDecorationBreak: 'clone',
      boxDecorationBreak: 'clone',
    }),
    [marginOffsetTop, dataToPaddingY]
  );

  const textBorderRadius = `${borderRadius?.topLeft || 0}px ${
    borderRadius?.topRight || 0
  }px ${borderRadius?.bottomRight || 0}px ${borderRadius?.bottomLeft || 0}px`;
  const textStyle = {
    ...bgColor,
    /* stylelint-disable-next-line */
    WebkitBoxDecorationBreak: 'clone',
    boxDecorationBreak: 'clone',
    position: 'relative',
    padding: `${paddingStyles.vertical} ${paddingStyles.horizontal}`,
    textAlign: styles.textAlign,
    borderRadius: textBorderRadius,
  };

  const backgroundTextStyle = {
    ...textStyle,
    color: 'transparent',
    ...(boxShadow ? { boxShadow } : null),
  };

  const foregroundTextStyle = {
    ...textStyle,
    background: 'none',
    ...(textShadow ? { textShadow } : null),
  };
  const shouldUseMeasuredLineBreaks =
    supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT ||
    hasTextGradient ||
    hasTextTexture ||
    hasMixedLineHeight;
  const measuredLineBreakContent = useMemo(
    () =>
      shouldUseMeasuredLineBreaks && !hasSemanticList(rawContent)
        ? getMeasuredLineBreakContent({
            element: outputElement,
            fontSizeToCss: dataToInlineFontSizeY,
            materializeFontSizes: hasMixedLineHeight,
            materializeLineHeights: hasMixedLineHeight,
            preserveColor:
              (hasMixedLineHeight ||
                supportedBackgroundTextMode ===
                  BACKGROUND_TEXT_MODE.HIGHLIGHT) &&
              !hasTextGradient &&
              !hasTextTexture,
          })
        : null,
    [
      outputElement,
      fontLoaded,
      supportedBackgroundTextMode,
      hasTextGradient,
      hasTextTexture,
      hasMixedLineHeight,
      shouldUseMeasuredLineBreaks,
      dataToInlineFontSizeY,
      rawContent,
    ]
  );
  const measuredLineBreakContentWithoutColor = useMemo(
    () =>
      supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT &&
      !hasTextGradient &&
      !hasTextTexture &&
      measuredLineBreakContent
        ? stripColorStylesFromMeasuredLineBreakContent(measuredLineBreakContent)
        : measuredLineBreakContent,
    [
      supportedBackgroundTextMode,
      hasTextGradient,
      hasTextTexture,
      measuredLineBreakContent,
    ]
  );
  const content = (measuredLineBreakContent || rawContent).replace(
    /\n$/,
    '\n\n'
  );
  // Remove inline color ownership so the active rendering layer controls the text fill.
  const contentWithoutColor = useMemo(
    () =>
      measuredLineBreakContentWithoutColor ||
      (hasList
        ? stripColorStylesFromMeasuredLineBreakContent(content)
        : getHTMLFormatters().setColor(content, createSolid(0, 0, 0))),
    [content, hasList, measuredLineBreakContentWithoutColor]
  );
  const renderContent =
    hasTextGradient || hasTextTexture ? contentWithoutColor : content;
  const RootElement = hasList ? 'div' : 'p';
  const ContentElement = hasList ? 'div' : 'span';
  const renderedContentWithoutColor = useMemo(
    () =>
      getListRenderContent({
        content: contentWithoutColor,
        listStyle: element.listStyle,
        font: element.font,
        fontMetrics: element.font?.metrics,
        fontSize: element.fontSize,
        fontStyle: element.fontStyle,
        fontWeight: element.fontWeight,
        lineHeight: element.lineHeight,
        dataToStyleX: dataToListStyleX,
        dataToStyleY: dataToListStyleY,
        dataToFontSizeY: dataToListFontSizeY,
        dataToMarkerStyleX: dataToListMarkerStyleX,
        dataToMarkerStyleY: dataToListMarkerStyleY,
        hideMarkers: true,
      }),
    [
      contentWithoutColor,
      dataToListFontSizeY,
      dataToListMarkerStyleX,
      dataToListMarkerStyleY,
      dataToListStyleX,
      dataToListStyleY,
      element.font,
      element.fontSize,
      element.fontStyle,
      element.fontWeight,
      element.lineHeight,
      element.listStyle,
    ]
  );
  const renderedContent = useMemo(
    () =>
      getListRenderContent({
        content: renderContent,
        color: element.color,
        listStyle: element.listStyle,
        font: element.font,
        fontMetrics: element.font?.metrics,
        fontSize: element.fontSize,
        fontStyle: element.fontStyle,
        fontWeight: element.fontWeight,
        lineHeight: element.lineHeight,
        dataToStyleX: dataToListStyleX,
        dataToStyleY: dataToListStyleY,
        dataToFontSizeY: dataToListFontSizeY,
        dataToMarkerStyleX: dataToListMarkerStyleX,
        dataToMarkerStyleY: dataToListMarkerStyleY,
      }),
    [
      dataToListFontSizeY,
      dataToListMarkerStyleX,
      dataToListMarkerStyleY,
      dataToListStyleX,
      dataToListStyleY,
      element.color,
      element.font,
      element.fontSize,
      element.fontStyle,
      element.fontWeight,
      element.lineHeight,
      element.listStyle,
      renderContent,
    ]
  );

  className = classnames(className, 'text-wrapper');
  if (supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT) {
    return (
      <>
        <RootElement className={className} style={highlightStyle}>
          <ContentElement style={marginStyle}>
            <ContentElement
              style={backgroundTextStyle}
              dangerouslySetInnerHTML={{
                __html: renderedContentWithoutColor,
              }}
            />
          </ContentElement>
        </RootElement>
        <RootElement
          className={className}
          style={highlightForegroundStyle}
          aria-hidden="true"
        >
          <ContentElement style={marginStyle}>
            <ContentElement
              style={foregroundTextStyle}
              dangerouslySetInnerHTML={{
                __html: renderedContent,
              }}
            />
          </ContentElement>
        </RootElement>
      </>
    );
  }
  return (
    <RootElement className={className} style={fillStyle}>
      <ContentElement dangerouslySetInnerHTML={{ __html: renderedContent }} />
    </RootElement>
  );
}

function getGradientFallbackColor(pattern) {
  const stopColor = pattern?.stops?.[0]?.color;
  if (!stopColor) {
    return null;
  }
  const { r, g, b, a } = stopColor;
  return generatePatternStyles(createSolid(r, g, b, a ?? 1), 'color').color;
}

function getTextureFallbackColor(textTexture, pattern) {
  if (textTexture?.fallbackColor) {
    return (
      generatePatternStyles(textTexture.fallbackColor, 'color').color ||
      '#000000'
    );
  }

  if (pattern && !hasGradient(pattern)) {
    return generatePatternStyles(pattern, 'color').color || '#000000';
  }

  return '#000000';
}

function getTextureBackgroundSize(sizing = 'cover') {
  if (sizing === 'stretch') {
    return '100% 100%';
  }

  return sizing;
}

function getTextureBackgroundPosition(position) {
  if (!position) {
    return 'center';
  }

  const x = typeof position.x === 'number' ? position.x : 0.5;
  const y = typeof position.y === 'number' ? position.y : 0.5;

  return `${x * 100}% ${y * 100}%`;
}

TextOutputWithUnits.propTypes = {
  element: StoryPropTypes.textContent.isRequired,
  dataToStyleX: PropTypes.func.isRequired,
  dataToStyleY: PropTypes.func.isRequired,
  dataToFontSizeY: PropTypes.func,
  dataToInlineFontSizeY: PropTypes.func,
  dataToPaddingX: PropTypes.func,
  dataToPaddingY: PropTypes.func,
  dataToListStyleX: PropTypes.func,
  dataToListStyleY: PropTypes.func,
  dataToListFontSizeY: PropTypes.func,
  dataToListMarkerStyleX: PropTypes.func,
  dataToListMarkerStyleY: PropTypes.func,
  className: PropTypes.string,
};

/**
 * Returns AMP HTML for saving into post content for displaying in the FE.
 *
 * @param {Object<*>} props Props.
 * @return {*} Rendered component.
 */
function TextOutput({ element }) {
  const { fontSize, width } = element;
  return (
    <TextOutputWithUnits
      element={element}
      className="fill"
      dataToStyleX={(x) => `${dataToEditorX(x, 100)}%`}
      dataToStyleY={(y) => `${dataToEditorY(y, 100)}%`}
      dataToFontSizeY={(y) =>
        `${dataToFontSize(y, 100, true /** withSafeArea */)}em`
      }
      dataToInlineFontSizeY={(y) => `${fontSize ? y / fontSize : 0}em`}
      // Both vertical and horizontal paddings are calculated in % relative to
      // the box's width per CSS rules.
      dataToPaddingX={(x) => `${(x / width) * 100}%`}
      dataToPaddingY={(y) => `${(y / width) * 100}%`}
      dataToListStyleX={(x) => `${width ? (x / width) * 100 : 0}%`}
      dataToListStyleY={(y) => `${fontSize ? y / fontSize : 0}em`}
      dataToListFontSizeY={(y) => `${fontSize ? y / fontSize : 0}em`}
      dataToListMarkerStyleX={(x, referenceSize) =>
        `${referenceSize ? x / referenceSize : 0}em`
      }
      dataToListMarkerStyleY={(y, referenceSize) =>
        `${referenceSize ? y / referenceSize : 0}em`
      }
    />
  );
}

TextOutput.propTypes = {
  element: StoryPropTypes.elements.text.isRequired,
};

export default TextOutput;
